import React, { useState } from 'react';
import { GAME_TINTS } from './GameCardArt';
import StorePhoto from './StorePhoto';
import { photoHintsOf } from '../lib/storePhotos';

// WO-30: art for set and series tiles of games without a logo we may use (Yu-Gi-Oh, MTG, Lorcana, One Piece, SWU).
// The API gives a set's `cover`: one of its own cards that we host (/cimg/cards/...). It is shown whole and upright,
// with nothing drawn over it (Scryfall's image rules for MTG: no crop of the artist/copyright line, no overlay).
// Without a cover the tile is drawn from the set's own data (game colour, set code, year, card count).
// Pokemon tiles keep their logos; these components are only used where a tile has no logo.

function yearOf(value) {
  const m = /^(\d{4})/.exec(String(value || ''));
  return m ? m[1] : null;
}

function codeOf(set) {
  const code = set?.code || set?.ptcgoCode;
  if (code) return /^\d+$/.test(code) ? `SET ${code}` : String(code).toUpperCase();   // Lorcana codes are numbers
  return String(set?.name || '?').split(/\s+/).filter(Boolean).slice(0, 3).map((w) => w[0]).join('').toUpperCase();
}

// WO-30 follow-up: server-rendered art (worker set_art: collage of the set's best hosted cards, or a typographic tile).
// Its edges fade to the page colour, so object-contain letterboxing is invisible.
function GeneratedArt({ art, alt, className = '', symbol = null, onFail }) {
  return (
    <div className={`relative overflow-hidden flex items-center justify-center bg-[#16161A] ${className}`} data-testid="generated-art">
      <img src={art.small} srcSet={`${art.small} 480w, ${art.large} 960w`} sizes="(max-width: 640px) 100vw, 480px"
           alt={alt || ''} loading="lazy" onError={onFail} className="w-full h-full object-contain" />
      {symbol && <img src={symbol} alt="" aria-hidden="true" className="absolute right-2 bottom-2 h-7 w-7 object-contain opacity-90" />}
    </div>
  );
}

function CoverCard({ cover, className = '', onFail }) {
  return (
    <img
      src={cover.image_small}
      alt={cover.name || ''}
      loading="lazy"
      onError={onFail}
      className={`h-full w-auto max-w-full object-contain rounded-[4%] shadow-[0_8px_24px_rgba(0,0,0,0.55)] ${className}`}
    />
  );
}

// WO-34: the set's own sealed product photo (a store listing's photo, hotlinked from its Shopify CDN, credited).
// WO-35: drawn in the shared store-photo frame (cream stage, white backgrounds melt in).
function SetPhoto({ photo, alt, className = '', onFail, showCredit = true }) {
  return (
    <div className={`relative overflow-hidden ${className}`}>
      <StorePhoto url={photo.url} alt={alt} credit={showCredit ? photo.credit : null} variant="tile"
                  hints={photoHintsOf(photo, { prefixed: false })}
                  testId="set-photo" onError={onFail} />
    </div>
  );
}

// Drawn tile: big set code over the game's colour, with two faint card outlines (no logos or trade dress).
export function DrawnSetTile({ gameId, title, subtitle, className = '' }) {
  const c = GAME_TINTS[gameId] || GAME_TINTS.pokemon;
  return (
    <div className={`relative overflow-hidden flex flex-col items-center justify-center ${className}`}
         style={{ background: `radial-gradient(circle at 30% 20%, ${c}40, transparent 60%), linear-gradient(160deg, ${c}26 0%, #16161A 70%)` }}
         role="img" aria-label={[title, subtitle].filter(Boolean).join(', ')} data-testid="drawn-set-tile">
      <svg viewBox="0 0 120 80" className="absolute right-2 bottom-2 h-3/5 opacity-30" aria-hidden="true">
        <rect x="58" y="8" width="38" height="56" rx="4" fill="none" stroke={c} strokeWidth="2" transform="rotate(12 77 36)" />
        <rect x="44" y="12" width="38" height="56" rx="4" fill={c} fillOpacity="0.15" stroke={c} strokeWidth="2" />
      </svg>
      <div className="relative font-mono font-semibold tracking-widest text-3xl sm:text-4xl" style={{ color: c }}>{title}</div>
      {subtitle && <div className="relative mt-1 text-xs uppercase tracking-widest text-cream/50">{subtitle}</div>}
    </div>
  );
}

export function SetCoverArt({ set, gameId, className = 'h-40' }) {
  const [broken, setBroken] = useState(false);
  const [artBroken, setArtBroken] = useState(false);
  const [photoBroken, setPhotoBroken] = useState(false);
  const c = GAME_TINTS[gameId] || GAME_TINTS.pokemon;
  if (set?.cover_photo?.url && !photoBroken) {
    return <SetPhoto photo={set.cover_photo} alt={set.name} className={className} onFail={() => setPhotoBroken(true)} />;
  }
  if (set?.cover_image?.small && !artBroken) {
    return <GeneratedArt art={set.cover_image} alt={set.name} symbol={set.images?.symbol} className={className}
                         onFail={() => setArtBroken(true)} />;
  }
  const year = yearOf(set?.releaseDate);
  const total = set?.total || set?.printedTotal || 0;
  if (set?.cover?.image_small && !broken) {
    return (
      <div className={`relative overflow-hidden flex items-center justify-center py-3 ${className}`}
           style={{ background: `radial-gradient(circle at 50% 35%, ${c}38, #16161A 72%)` }} data-testid="set-cover">
        <CoverCard cover={set.cover} onFail={() => setBroken(true)} />
      </div>
    );
  }
  const sub = [year, total ? `${total} cards` : null].filter(Boolean).join(' · ');
  return <DrawnSetTile gameId={gameId} title={codeOf(set)} subtitle={sub} className={className} />;
}

// Series (another game's release year): the covers of its most valuable sets side by side, never overlapping.
export function SeriesCoverArt({ series, gameId, className = 'h-48' }) {
  const [failed, setFailed] = useState({});
  const [artBroken, setArtBroken] = useState(false);
  const [photoFailed, setPhotoFailed] = useState({});
  const photos = (series?.cover_photos || []).filter((p) => p?.url && !photoFailed[p.url]).slice(0, 3);
  if (photos.length) {   // WO-34: the group's set photos side by side
    return (
      <div className={`relative overflow-hidden grid gap-0 bg-[#16161A] ${className}`} data-testid="series-photos"
           style={{ gridTemplateColumns: `repeat(${photos.length}, minmax(0, 1fr))` }}>
        {photos.map((p) => (
          <SetPhoto key={p.url} photo={p} alt="" className="h-full"
                    onFail={() => setPhotoFailed((f) => ({ ...f, [p.url]: true }))} />
        ))}
      </div>
    );
  }
  if (series?.cover_image?.small && !artBroken) {
    return <GeneratedArt art={series.cover_image} alt={series.name} className={className} onFail={() => setArtBroken(true)} />;
  }
  const c = GAME_TINTS[gameId] || GAME_TINTS.pokemon;
  const covers = (series?.covers || []).filter((cv) => cv?.image_small && !failed[cv.product_id]).slice(0, 3);
  if (covers.length) {
    return (
      <div className={`relative overflow-hidden flex items-center justify-center gap-2 sm:gap-3 px-3 py-4 ${className}`}
           style={{ background: `radial-gradient(circle at 50% 30%, ${c}38, #16161A 72%)` }} data-testid="series-covers">
        {covers.map((cv) => (
          <div key={cv.product_id} className="h-full flex-1 min-w-0 flex items-center justify-center">
            <CoverCard cover={cv} onFail={() => setFailed((f) => ({ ...f, [cv.product_id]: true }))} />
          </div>
        ))}
      </div>
    );
  }
  const sub = series?.setCount ? `${series.setCount} sets` : null;
  return <DrawnSetTile gameId={gameId} title={series?.name || '?'} subtitle={sub} className={className} />;
}

export default SetCoverArt;
